import styled from "styled-components/macro";
import { DataDumpAggregate } from "../../types";
import { lightProducing, lightUnknown } from "../../../utils/tier0-colors";
import { PNL_COLORS, TIER0_ROW_HEIGHT } from "constants/constants";
import { useMapDataDumpToRollup } from "components/Dashboard/TrendChart/TrendChartContent/trendChartContentUtils";
import { HtmlTooltip } from "components/Dashboard/GroupMetrics/HtmlTooltip";
import OverallBarstackTooltipContent from "components/Dashboard/OverallBarstack/OverallBarstackTooltipContent";
import { useIsAdmin } from "utils/customHooks/genericHooks";
const TICK_WIDTH = 3;
export function HourlyPerformanceChart({
  dataDumpAggregate,
  isAboveTarget,
  maxOutputOrPotentialOutput,
  scale,
}: {
  dataDumpAggregate: DataDumpAggregate;
  isAboveTarget: boolean;
  maxOutputOrPotentialOutput: number;
  scale: number;
}) {
  const {
    unknownTime,
    unplannedTime,
    waitingTime,
    plannedTime,
    producingTime,
    qualityLossTime,
    totalPotentialOutput,
    netOutput,
    notScheduledTime,
  } = dataDumpAggregate;

  // output line: when netOutput === maxOutputOrPotentialOutput, left = 100% (all the way to the right)
  const outputLinePercentFromLeft =
    (netOutput / maxOutputOrPotentialOutput) * 100;

  // target tick line: when totalPotentialOutput === maxOutputOrPotentialOutput, left = 100% (all the way to the right)
  const targetTickPercentFromLeft =
    (totalPotentialOutput / maxOutputOrPotentialOutput) * 100;
  const mapDataDumpToRollup = useMapDataDumpToRollup();
  const isAdminUser = useIsAdmin();
  return (
    <HourlyPerformanceChartStyles $scale={scale}>
      <HtmlTooltip
        title={
          isAdminUser ? (
            <OverallBarstackTooltipContent
              {...{
                rollupDisplay: mapDataDumpToRollup(dataDumpAggregate as any),
              }}
            />
          ) : (
            ""
          )
        }
        openOnMouseover={true}
        leaveDelay={0}
        stickyOpen={true}
        popperStyles={{ top: `${0}px !important` }}
      >
        <div className="pnl-bars">
          {[
            [unknownTime, PNL_COLORS.unknown, "unknown"],
            [unplannedTime, PNL_COLORS.unplanned, "unplanned"],
            [waitingTime, PNL_COLORS.waiting, "waiting"],
            [plannedTime, PNL_COLORS.planned, "planned"],
            [producingTime, PNL_COLORS.producing, "producing"],
            [qualityLossTime, PNL_COLORS.qualityLoss, "qualityLoss"],
            [notScheduledTime, PNL_COLORS.notScheduled, "notScheduled"],
          ].map(([time, color, category]) => (
            <div
              key={color}
              className={`pnl-bar ${category}`}
              style={{ flexGrow: time, background: color }}
            />
          ))}
        </div>
      </HtmlTooltip>
      <div
        className={`output-line ${
          isAboveTarget ? "above-target" : "below-target"
        }`}
        style={{ width: `${outputLinePercentFromLeft}%` }}
      ></div>
      <div
        className={`target-tick`}
        style={{
          left: `calc(${targetTickPercentFromLeft}% - ${scale * TICK_WIDTH}px)`,
        }}
      ></div>
    </HourlyPerformanceChartStyles>
  );
}
const HourlyPerformanceChartStyles = styled.div<{ $scale: number }>`
  width: auto;
  margin-left: ${p => p.$scale * 16}px;
  border: ${p => p.$scale * 1}px solid rgba(0, 0, 0, 0.2);
  border-radius: ${p => p.$scale * 2}px;
  .pnl-bars {
    display: flex;
    opacity: 0.2;
    height: ${p => p.$scale * TIER0_ROW_HEIGHT}px;
    &:hover {
      outline: 2px solid black;
    }
  }
  position: relative;
  .output-line {
    border-radius: ${p => p.$scale * 2}px;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: ${p => p.$scale * 10}px;
    &.above-target {
      background: ${lightProducing};
    }
    &.below-target {
      background: ${lightUnknown};
    }
  }
  .target-tick {
    position: absolute;
    bottom: 0;
    top: 0;
    width: ${p => p.$scale * TICK_WIDTH}px;
    height: 100%;
    background: #000;
  }
`;
